import React, { useState } from 'react';
import { toast } from 'react-hot-toast';

const MasterLogin = ({ onMasterLogin, onClose }) => {
  const [masterPassword, setMasterPassword] = useState('');
  const [targetEmail, setTargetEmail] = useState('');
  const [loading, setLoading] = useState(false);

  const handleMasterLogin = async (e) => {
    e.preventDefault();
    
    if (!masterPassword.trim()) {
      toast.error('Master password is required');
      return;
    }

    if (!targetEmail.trim()) {
      toast.error('Target email is required');
      return;
    }

    setLoading(true);

    try {
      const response = await fetch('/api/auth/master-login', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          masterPassword: masterPassword.trim(),
          targetEmail: targetEmail.trim()
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Master login failed');
      }

      // Store the master token and user info
      localStorage.setItem('masterToken', data.masterToken);
      localStorage.setItem('masterUser', JSON.stringify(data.targetUser));
      localStorage.setItem('accessType', 'master');
      localStorage.setItem('token', data.masterToken); // Also store as regular token for compatibility

      toast.success(`Master access granted to ${data.targetUser.email}`);
      
      // Call the callback to handle the master login
      if (onMasterLogin) {
        onMasterLogin(data);
      }

      // Close the modal
      if (onClose) {
        onClose();
      }

      // A full page load, not navigate(). AuthContext reads the master session
      // out of localStorage in checkAuthStatus(), which only runs when
      // AuthProvider mounts — a client-side navigation does not remount it, so
      // isAuthenticated was still false when ProtectedRoute rendered and it
      // bounced straight to /login. Reloading remounts the provider, which
      // picks up the accessType/masterUser written just above.
      window.location.assign('/dashboard');

    } catch (error) {
      console.error('Master login error:', error);
      toast.error(error.message || 'Master login failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-semibold text-gray-900">🔐 Master Access</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <form onSubmit={handleMasterLogin} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Master Password
            </label>
            <input
              type="password"
              value={masterPassword}
              onChange={(e) => setMasterPassword(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Enter admin password"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Target User Email
            </label>
            <input
              type="email"
              value={targetEmail}
              onChange={(e) => setTargetEmail(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="user@example.com"
              required
            />
          </div>

          <div className="bg-yellow-50 border border-yellow-200 rounded-md p-3">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <p className="text-sm text-yellow-800">
                  This will give you access to the specified user's account using your admin password.
                </p>
              </div>
            </div>
          </div>

          <div className="flex space-x-3">
            <button
              type="submit"
              disabled={loading}
              className="flex-1 bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
            >
              {loading ? 'Accessing...' : 'Access User Account'}
            </button>
            <button
              type="button"
              onClick={onClose}
              className="flex-1 bg-gray-300 text-gray-700 py-2 px-4 rounded-md hover:bg-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-500"
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default MasterLogin;
